import { z } from 'zod/v4';

import { ComplianceStatus, ComplianceStatusRuntype, ElasticTotalHitsDTORuntype } from '../backendTypes';
import {
    ConflictMineralStatus,
    CustomPartFullSpecificationRuntype,
    DielectricCeramicEnum,
    DielectricRuntype,
    DielectricTypes,
    GenericFullPartRuntype,
    LifecycleEnum,
    LifecycleYteolBucketsEnum,
    OffTheShelfPartWithFullPackageRuntype,
    OtsFullPartRuntype,
    PackageDTORuntype,
    PackageUserInputRuntype,
    PreferenceStatusEnumRuntype,
    StandardPartTypes,
    TechnicalParametersRuntype,
} from '../part/partBackendTypes';
import { RegionsEnum } from '../supplierAndStockLocation';

export type PartMatchTypeFullResponse = z.infer<typeof PartMatchTypeFullResponseRuntype>;
export const PartMatchTypeFullResponseRuntype = z.union([
    z.object({
        type: z.literal('OffTheShelf'),
        data: OtsFullPartRuntype,
    }),
    z.object({
        type: z.literal('Generic'),
        data: GenericFullPartRuntype,
    }),
]);

export type EmsPartNumberPartSpecification = z.infer<typeof EmsPartNumberPartSpecificationRuntype>;
const EmsPartNumberPartSpecificationRuntype = z.object({
    id: z.string(),
    mpn: z.string().nullable().optional(),
    manufacturer: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    package: z.string().nullable().optional(),
    part_type: z.string().nullable().optional(),
});

const MatchingTypeRuntype = z.object({
    type: z.string(),
    id: z.string().nullable().optional(),
});

export type MatchingReason = z.infer<typeof MatchingReasonRuntype>;
export const MatchingReasonRuntype = z.object({
    matching_type: z.union([z.literal('Automatic'), z.literal('Manual')]),
    reason: MatchingTypeRuntype.nullable().optional(),
});

export interface EmsPartNumberPartMatchFullPart extends z.infer<typeof EmsPartNumberPartMatchFullPartRuntype> {}
const EmsPartNumberPartMatchFullPartRuntype = z.object({
    ipn: z.string(),
    part: PartMatchTypeFullResponseRuntype,
    matching_reason: MatchingReasonRuntype,
});

/**
 * The ids of the OTS part records linked to an IPN component (its OffTheShelf matches).
 *
 * Generic matches are dropped.
 */
export function linkedOtsPartIdsFromMatches(matches: EmsPartNumberPartMatchFullPart[]): string[] {
    return matches.filter((m) => m.part.type === StandardPartTypes.OffTheShelf).map((m) => m.part.data.id);
}

export type IpnSuggestableIncompleteGenericFullPart = Extract<
    IpnSuggestablePartFullResponse,
    { type: 'IncompleteGeneric' }
>;
export type IpnSuggestablePartFullResponse = z.infer<typeof IpnSuggestablePartFullResponseRuntype>;
const IpnSuggestablePartFullResponseRuntype = z.union([
    z.object({
        type: z.literal(StandardPartTypes.Generic),
        data: GenericFullPartRuntype,
    }),
    z.object({
        type: z.literal('IncompleteGeneric'),
        data: TechnicalParametersRuntype,
    }),
    z.object({
        type: z.literal(StandardPartTypes.OffTheShelf),
        data: OtsFullPartRuntype,
    }),
]);

const IpnSuggestionReasonRuntype = z.object({
    type: z.union([
        z.literal('Description'),
        z.literal('Mpn'),
        z.literal('DescriptionMpn'),
        z.literal('DescriptionSpn'),
    ]),
    id: z.string(),
});

export interface IpnSuggestionFullPart extends z.infer<typeof IpnSuggestionFullPartRuntype> {}
const IpnSuggestionFullPartRuntype = z.object({
    ipn: z.string(),
    part: IpnSuggestablePartFullResponseRuntype,
    matching_reason: IpnSuggestionReasonRuntype,
});

export interface CustomerPartNumber extends z.infer<typeof CustomerPartNumberRuntype> {}
const CustomerPartNumberRuntype = z.object({
    id: z.string(),
    value: z.string(),
    customer: z.string(),
    revision: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});

export interface SupplierPartNumber extends z.infer<typeof SupplierPartNumberRuntype> {}
const SupplierPartNumberRuntype = z.object({
    supplier_part_number: z.string().optional(),
    supplier: z.string().nullable(),
});

const CapacitorFunctionRuntype = z.object({
    type: z.literal('Capacitor'),
    technical_parameters: z.object({
        capacitance: z.string().nullable(), // Strings because these values are decimal
        dielectric: DielectricRuntype.nullable(),
        tolerance: z.string().nullable(),
        voltage_rating: z.string().nullable(),
    }),
});

const ResistorFunctionRuntype = z.object({
    type: z.literal('Resistor'),
    technical_parameters: z.object({
        resistance: z.string().nullable(), // Strings because these values are decimal
        power_rating: z.string().nullable(),
        tolerance: z.string().nullable(),
        temperature_coefficient: z.string().nullable(),
        voltage_rating: z.string().nullable(),
    }),
});

const UnsupportedFunctionRuntype = z.object({
    type: z.literal('Unsupported'),
    technical_parameters: z.object({
        part_type: z.string().nullable(),
    }),
});

export type ComponentFunction = z.infer<typeof FunctionSpecificationRuntype>;
const FunctionSpecificationRuntype = z.union([
    CapacitorFunctionRuntype,
    ResistorFunctionRuntype,
    UnsupportedFunctionRuntype,
]);

export interface ComponentSpecification extends z.infer<typeof ComponentSpecificationRuntype> {}
export const ComponentSpecificationRuntype = z.object({
    form_and_fit: z.string().nullable(),
    function_specification: FunctionSpecificationRuntype.nullable(),
});

export interface ComponentSpecificationWithFullPackage extends z.infer<
    typeof ComponentSpecificationWithFullPackageRuntype
> {}
export const ComponentSpecificationWithFullPackageRuntype = z.object({
    form_and_fit: PackageDTORuntype.nullable(),
    function_specification: FunctionSpecificationRuntype.nullable(),
});

export interface SpecificationsConflict extends z.infer<typeof SpecificationsConflictRuntype> {}
export const SpecificationsConflictRuntype = z.object({
    form_and_fit: z.boolean(),
    function_specification_type: z.boolean(),
    function_specification_keys: z.array(z.string()),
});

export enum ComponentState {
    Active = 'Active',
    Removed = 'Removed',
}

export enum ComponentTypeEnum {
    Custom = 'Custom',
    OffTheShelf = 'OffTheShelf',
}

export enum ErpDataChangesFilter {
    ReviewRequired = 'ReviewRequired',
    NoReviewRequired = 'NoReviewRequired',
}

export enum SuggestionsFilter {
    Available = 'Available',
    NotAvailable = 'NotAvailable',
}

export enum LinkedPartsFilter {
    LinkedParts = 'LinkedParts',
    NoLinkedParts = 'NoLinkedParts',
    IncompleteLinkedParts = 'IncompleteLinkedParts',
}

export enum IpnLastChanged {
    Today = 'Today',
    Yesterday = 'Yesterday',
    Last7Days = 'Last7Days',
    MoreThan7DaysAgo = 'MoreThan7DaysAgo',
}

const PartMappedToIpnWithFullPackageRuntype = z.object({
    OffTheShelf: OffTheShelfPartWithFullPackageRuntype.optional(),
    Generic: GenericFullPartRuntype.optional(),
});

export const IpnFullPartMatchRuntype = z.object({
    id: z.string(),
    ipn: z.string(),
    part: PartMappedToIpnWithFullPackageRuntype,
    matching_reason: MatchingReasonRuntype,
});

export type ComponentOrigin = z.infer<typeof ComponentOriginRuntype>;
const ComponentOriginRuntype = z.union([
    z.object({
        type: z.literal('Import'),
    }),
    z.object({
        type: z.literal('Manual'),
    }),
    z.object({
        type: z.literal('UserDefined'),
        context: z.object({
            context: z.string(),
        }),
    }),
]);

export interface OtsComponentFull extends z.infer<typeof OtsComponentFullRuntype> {}
export const OtsComponentFullRuntype = z.object({
    id: z.string(),
    is_preferred: z.boolean(),
    part_specifications: z.array(EmsPartNumberPartSpecificationRuntype),
    matches: z.array(EmsPartNumberPartMatchFullPartRuntype),
    suggestions: z.array(IpnSuggestionFullPartRuntype),
    state: z.enum(ComponentState),
    updated_at: z.string(),
    last_imported_at: z.string(),
    cpns: z.array(CustomerPartNumberRuntype),
    has_unreviewed_erp_changes: z.boolean(),
    is_restricted_to_customers: z.boolean(),
    reach_compliant: z.enum(ComplianceStatus),
    rohs_compliant: z.enum(ComplianceStatus),
    aecq_compliant: z.enum(ComplianceStatus),
    china_rohs_compliant: z.enum(ComplianceStatus),
    eu_pop_compliant: z.enum(ComplianceStatus),
    pfas_compliant: z.enum(ComplianceStatus),
    prop65_compliant: z.enum(ComplianceStatus),
    conflict_mineral_status: z.enum(ConflictMineralStatus),
    lifecycle_status: z.enum(LifecycleEnum),
    lifecycle_yteol: z.number().nullable(),
    manufacturer_preference_status: PreferenceStatusEnumRuntype.nullable(),
    component_specification: ComponentSpecificationWithFullPackageRuntype.optional(),
    component_specifications: z
        .object({
            edited: ComponentSpecificationWithFullPackageRuntype.optional(),
            imported: ComponentSpecificationWithFullPackageRuntype.optional(),
            inferred: ComponentSpecificationWithFullPackageRuntype.optional(),
            conflicts: SpecificationsConflictRuntype.optional(),
        })
        .optional(),
    spns: z.array(SupplierPartNumberRuntype),
    linked_parts_filter: z.enum(LinkedPartsFilter),
    std_factory_lead_time: z.number().nullable(),
    category: z.string().nullable().optional(),
    origin: ComponentOriginRuntype,
});

const PartMatchTypeResponseRuntype = z.union([
    z.object({
        type: z.literal(StandardPartTypes.OffTheShelf),
        id: z.string(),
    }),
    z.object({
        type: z.literal(StandardPartTypes.Generic),
        id: z.string(),
    }),
]);

export interface EmsPartNumberPartMatchResponse extends z.infer<typeof EmsPartNumberPartMatchResponseRuntype> {}
export const EmsPartNumberPartMatchResponseRuntype = z.object({
    ipn: z.string(),
    part: PartMatchTypeResponseRuntype,
    matching_reason: MatchingReasonRuntype,
});

export interface EmsPartNumberPartMatchRequest extends z.infer<typeof EmsPartNumberPartMatchRequestRuntype> {}
export const EmsPartNumberPartMatchRequestRuntype = z.object({
    ipn: z.string(),
    // With autosaving we're only sending one part id per request
    // ideally we'd have a raw_specification_id per part_match_id
    raw_specification_id: z.string().optional(),
    part_matches: z.object({
        off_the_shelf: z.array(z.string()),
        generic: z.array(z.string()),
    }),
});

export type CustomComponentFull = z.infer<typeof CustomComponentFullRuntype>;
export const CustomComponentFullRuntype = z.object({
    id: z.string(),
    is_preferred: z.boolean(),
    state: z.enum(ComponentState),
    updated_at: z.string(),
    description: z.string().nullable(),
    last_imported_at: z.string(),
    cpns: z.array(CustomerPartNumberRuntype),
    is_restricted_to_customers: z.boolean(),
    matches: z.array(CustomPartFullSpecificationRuntype),
    component_specification: PackageDTORuntype.nullable(),
    component_specifications: z
        .object({
            edited_form_and_fit: PackageDTORuntype.nullable(),
            imported_form_and_fit: PackageDTORuntype.nullable(),
            inferred_form_and_fit: PackageDTORuntype.nullable(),
        })
        .nullable(),
    category: z.string().nullable().optional(),
    origin: ComponentOriginRuntype,
});

export type OtsOrCustomComponent = z.infer<typeof OtsOrCustomComponentRuntype>;
export const OtsOrCustomComponentRuntype = z.union([
    z.object({
        type: z.literal('OffTheShelf'),
        data: OtsComponentFullRuntype,
    }),
    z.object({
        type: z.literal('Custom'),
        data: CustomComponentFullRuntype,
    }),
]);

const NumericOptionRuntype = z.object({
    count: z.number(),
    value: z.number(),
});

const StringOptionRuntype = z.object({
    count: z.number(),
    value: z.string(),
});

export type ComponentSearchSortRequest = z.infer<typeof ComponentSearchSortRequestRuntype>;
export const ComponentSearchSortRequestRuntype = z.union([
    z.object({
        field: z.literal('std-factory-lead-time'),
        direction: z.union([z.literal('asc'), z.literal('desc')]),
    }),
    z.object({
        field: z.literal('last-changed'),
        direction: z.union([z.literal('asc'), z.literal('desc')]),
    }),
    z.object({
        field: z.literal('lifecycle'),
        direction: z.union([z.literal('asc'), z.literal('desc')]),
    }),
]);

export type ComponentSearchFilterRequest = z.infer<typeof ComponentSearchFilterRequestRuntype>;
export const ComponentSearchFilterRequestRuntype = z.union([
    z.object({
        field: z.literal('capacitance'),
        operator: z.literal('inNumberRange'),
        parameter: z.array(z.union([z.number(), z.null()])),
    }),
    z.object({
        field: z.literal('resistance'),
        operator: z.literal('inNumberRange'),
        parameter: z.array(z.union([z.number(), z.null()])),
    }),
    z.object({
        field: z.literal('power-rating'),
        operator: z.literal('inNumberRange'),
        parameter: z.array(z.union([z.number(), z.null()])),
    }),
    z.object({
        field: z.literal('tolerance'),
        operator: z.literal('inNumberRange'),
        parameter: z.array(z.union([z.number(), z.null()])),
    }),
    z.object({
        field: z.literal('temperature-coefficient'),
        operator: z.literal('inNumberRange'),
        parameter: z.array(z.union([z.number(), z.null()])),
    }),
    z.object({
        field: z.literal('voltage-rating'),
        operator: z.literal('inNumberRange'),
        parameter: z.array(z.union([z.number(), z.null()])),
    }),
    z.object({
        field: z.literal('dielectric-dielectric'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(DielectricCeramicEnum)),
    }),
    z.object({
        field: z.literal('dielectric-type'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(DielectricTypes)),
    }),
    z.object({
        field: z.literal('manufacturer'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('package'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('lifecycle'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(LifecycleEnum)),
    }),
    z.object({
        field: z.literal('lifecycle-yteol'),
        operator: z.literal('inNumberRange'),
        parameter: z.array(z.union([z.number(), z.null()])),
    }),
    z.object({
        field: z.literal('reach'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComplianceStatus)),
    }),
    z.object({
        field: z.literal('rohs'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComplianceStatus)),
    }),
    z.object({
        field: z.literal('aecq'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComplianceStatus)),
    }),
    z.object({
        field: z.literal('china-rohs-compliance'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComplianceStatus)),
    }),
    z.object({
        field: z.literal('eu-pop-compliance'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComplianceStatus)),
    }),
    z.object({
        field: z.literal('pfas-compliance'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComplianceStatus)),
    }),
    z.object({
        field: z.literal('prop65-compliance'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComplianceStatus)),
    }),
    z.object({
        field: z.literal('conflict-mineral-status'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ConflictMineralStatus)),
    }),
    z.object({
        field: z.literal('countries-of-origin'),
        operator: z.literal('arrIncludesSome'),
        parameter: z.array(z.enum(RegionsEnum)),
    }),
    z.object({
        field: z.literal('ipn-state'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComponentState)),
    }),
    z.object({
        field: z.literal('component-type'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(ComponentTypeEnum)),
    }),
    z.object({
        field: z.literal('ipn'),
        operator: z.literal('includesString'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('cpn'),
        operator: z.literal('includesString'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('mpn'),
        operator: z.literal('includesString'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('spn'),
        operator: z.literal('includesString'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('linked-parts'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(LinkedPartsFilter)),
    }),
    z.object({
        field: z.literal('last-changed'),
        operator: z.literal('equalsAny'),
        parameter: z.array(z.enum(IpnLastChanged)),
    }),
    z.object({
        field: z.literal('description'),
        operator: z.literal('includesString'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('linked-parts'),
        operator: z.literal('includesString'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('category'),
        operator: z.literal('includesString'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('erp-data-changes'),
        operator: z.literal('arrIncludesSome'),
        parameter: z.array(z.enum(ErpDataChangesFilter)),
    }),
    z.object({
        field: z.literal('customer'),
        operator: z.literal('arrIncludesSome'),
        parameter: z.array(z.string()),
    }),
    z.object({
        field: z.literal('rfq'),
        operator: z.literal('arrIncludesSome'),
        parameter: z.array(z.string()),
    }),
]);

export type ComponentSearchAggregationDTO = z.infer<typeof AggregationsRuntype>;
export const AggregationsRuntype = z.object({
    capacitance: z.object({ field: z.literal('capacitance'), options: z.array(NumericOptionRuntype) }),
    dielectric: z.array(z.object({ field: z.string(), options: z.array(StringOptionRuntype) })),
    has_unreviewed_erp_changes: z.object({
        field: z.literal('has_unreviewed_erp_changes'),
        options: z.array(StringOptionRuntype),
    }),
    ipn_state: z.object({
        field: z.literal('ipn_state'),
        options: z.array(
            z.object({
                count: z.number(),
                value: z.enum(ComponentState),
            }),
        ),
    }),
    last_changed: z.object({
        field: z.literal('last_changed'),
        options: z.array(
            z.object({
                count: z.number(),
                value: z.enum(IpnLastChanged),
            }),
        ),
    }),
    last_imported_at: z.object({ field: z.literal('last_imported_at'), value: z.number().nullable() }),
    linked_parts_filter: z.object({
        field: z.literal('linked_parts_filter'),
        options: z.array(
            z.object({
                count: z.number(),
                value: z.enum(LinkedPartsFilter),
            }),
        ),
    }),
    manufacturers: z.object({ field: z.literal('manufacturers'), options: z.array(StringOptionRuntype) }),
    packages: z.object({ field: z.literal('packages'), options: z.array(StringOptionRuntype) }),
    'part-types': z.object({ field: z.literal('part-types'), options: z.array(StringOptionRuntype) }),
    category: z.object({ field: z.literal('category'), options: z.array(StringOptionRuntype) }),
    power_rating: z.object({ field: z.literal('power_rating'), options: z.array(NumericOptionRuntype) }),
    resistance: z.object({ field: z.literal('resistance'), options: z.array(NumericOptionRuntype) }),
    temperature_coefficient: z.object({
        field: z.literal('temperature_coefficient'),
        options: z.array(NumericOptionRuntype),
    }),
    tolerance: z.object({ field: z.literal('tolerance'), options: z.array(NumericOptionRuntype) }),
    voltage_rating: z.object({ field: z.literal('voltage_rating'), options: z.array(NumericOptionRuntype) }),
    suggestions: z.object({
        field: z.literal('suggestions'),
        options: z.array(StringOptionRuntype),
    }),
    lifecycle: z.object({
        field: z.literal('lifecycle'),
        options: z.array(
            z.object({
                count: z.number(),
                value: z.enum(LifecycleEnum),
            }),
        ),
    }),
    reach: z.object({
        field: z.literal('reach'),
        options: z.array(
            z.object({
                count: z.number(),
                value: z.enum(ComplianceStatus),
            }),
        ),
    }),
    rohs: z.object({
        field: z.literal('rohs'),
        options: z.array(
            z.object({
                count: z.number(),
                value: z.enum(ComplianceStatus),
            }),
        ),
    }),
    aecq: z.object({
        field: z.literal('aecq'),
        options: z.array(
            z.object({
                count: z.number(),
                value: z.enum(ComplianceStatus),
            }),
        ),
    }),
    countries_of_origin: z.object({
        field: z.literal('countries_of_origin'),
        options: z.array(
            z.object({
                count: z.number(),
                value: z.enum(RegionsEnum),
            }),
        ),
    }),
});

export const ComponentsSearchResponseDTORuntype = z.object({
    ipns: z.array(OtsOrCustomComponentRuntype),
    total_hits: ElasticTotalHitsDTORuntype,
    highlights: z.record(z.string(), z.array(z.string())).optional(),
    aggregations: AggregationsRuntype,
    page_params: z.number().or(z.undefined()).or(z.null()),
});

export const ComponentsAggregationsResponseDTORuntype = z.object({
    aggregations: AggregationsRuntype.nullable(),
});

export interface IpnFormAndFitFormPatchValues extends z.infer<typeof IpnFormAndFitFormPatchValuesRuntype> {}
export const IpnFormAndFitFormPatchValuesRuntype = z.object({
    form_and_fit: PackageUserInputRuntype.optional(),
    rfq_context: z.literal('WithinRfQ'),
    rfq_id: z.string(),
    origin: z.object({ type: z.literal('Manual') }),
});

export type ComponentsHealthSummary = z.infer<typeof ComponentsHealthSummaryRuntype>;
export const ComponentsHealthSummaryRuntype = z.object({
    reach_compliance: z.array(
        z.object({
            count: z.number(),
            status_type: ComplianceStatusRuntype,
        }),
    ),
    rohs_compliance: z.array(
        z.object({
            count: z.number(),
            status_type: ComplianceStatusRuntype,
        }),
    ),
    aecq_compliance: z.array(
        z.object({
            count: z.number(),
            status_type: ComplianceStatusRuntype,
        }),
    ),
    lifecycle: z.array(
        z.object({
            status_type: z.enum(LifecycleEnum),
            count: z.number(),
        }),
    ),
    lifecycle_yteol: z.array(
        z.object({
            status_type: z.enum(LifecycleYteolBucketsEnum),
            count: z.number(),
        }),
    ),
});

export type ComponentsLastImport = z.infer<typeof ComponentsLastImportRuntype>;

export const ComponentsLastImportRuntype = z.object({
    last_imported_at: z.string().nullable(),
    search_index_updated_at: z.string().nullable(),
});

/** Exact and case-sensitive; without a revision it finds the revision-less IPN plus every revision of the value. */
export const FindIpnsQueryRuntype = z.object({
    type: z.literal('ipn'),
    value: z.string(),
    revision: z.string().nullish(),
});
export type FindIpnsQueryDTO = z.infer<typeof FindIpnsQueryRuntype>;

/** All three kinds are importable IPNs; the kind is for display, not a filter. */
export const FindIpnsEntityRuntype = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('ots_component'),
        ipn_id: z.string(),
        ipn_value: z.string(),
        ipn_revision: z.string().nullable(),
    }),
    z.object({
        kind: z.literal('custom_component'),
        ipn_id: z.string(),
        ipn_value: z.string(),
        ipn_revision: z.string().nullable(),
        description: z.string().nullable(),
    }),
    z.object({
        kind: z.literal('assembly'),
        ipn_id: z.string(),
        ipn_value: z.string(),
        ipn_revision: z.string().nullable(),
        assembly_id: z.string(),
        designator: z.string(),
    }),
]);
export type FindIpnsEntityDTO = z.infer<typeof FindIpnsEntityRuntype>;

/** `removed` IPNs are returned like any other; no state filter is applied. */
export const FindIpnsStateRuntype = z.enum(['active', 'removed']);
export type FindIpnsStateDTO = z.infer<typeof FindIpnsStateRuntype>;

export const FindIpnsMatchedFieldRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ipn') }),
    z.object({ type: z.literal('ipn_with_revision') }),
]);
export type FindIpnsMatchedFieldDTO = z.infer<typeof FindIpnsMatchedFieldRuntype>;

export const FindIpnsMatchReasonRuntype = z.object({
    /** Describes the query: `exact` if it carried a revision, `base_ipn` if not, which fans out to every revision. */
    method: z.enum(['exact', 'base_ipn']),
    /** Describes the MATCHED ROW: whether the IPN it found carries a revision. */
    matched_field: FindIpnsMatchedFieldRuntype,
});
export type FindIpnsMatchReasonDTO = z.infer<typeof FindIpnsMatchReasonRuntype>;

export interface FindIpnsMatchDTO extends z.infer<typeof FindIpnsMatchRuntype> {}
export const FindIpnsMatchRuntype = z.object({
    entity: FindIpnsEntityRuntype,
    state: FindIpnsStateRuntype,
    match_reason: FindIpnsMatchReasonRuntype,
});

/** One result per query in request order, misses too, so match by position: the echoed query is normalized. */
export interface FindIpnsResponseDTO extends z.infer<typeof FindIpnsResponseRuntype> {}
export const FindIpnsResponseRuntype = z.object({
    results: z.array(
        z.object({
            query: FindIpnsQueryRuntype,
            matches: z.array(FindIpnsMatchRuntype),
        }),
    ),
});
